import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Glow } from '@/components/generals/glow'
import { Reveal } from '@/components/generals/reveal'
import { Wordmark } from '@/components/generals/wordmark'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { AnimatedHeading } from '@/components/generals/animated-heading'

const ComingSoon: FC = (): ReactNode => {
    return (
        <main className='relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-background px-6 py-24'>
            <Seo
                title='Coming soon'
                description='Something new from Funnel is on the way. Join the list and be the first to know.'
            />

            <Glow variant='hero' />

            <div className='relative z-1 flex w-full max-w-[640px] flex-col items-center gap-8 text-center'>
                <Reveal
                    immediate
                    delay={0.1}
                    y={14}
                    blur={6}
                    duration={0.7}
                >
                    <Link
                        href='/'
                        aria-label='Funnel, back to home'
                        className='inline-flex transition-opacity duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:opacity-70'
                    >
                        <Wordmark />
                    </Link>
                </Reveal>

                <AnimatedHeading
                    as='h1'
                    immediate
                    delay={0.25}
                    segments={[
                        { text: 'Something good is' },
                        { text: 'on the way.', serif: true }
                    ]}
                    className='text-[clamp(2.25rem,5.4vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.04em] text-white'
                />

                <Reveal
                    immediate
                    delay={0.5}
                    y={16}
                    blur={8}
                    duration={0.8}
                    className='max-w-[48ch] text-[clamp(1rem,1.2vw,1.125rem)] leading-[1.55] tracking-[-0.01em] text-white/56'
                >
                    We&rsquo;re putting the finishing touches to this one. Leave your email and you&rsquo;ll be the first to see it.
                </Reveal>

                <Reveal
                    immediate
                    delay={0.65}
                    y={16}
                    blur={8}
                    duration={0.8}
                    className='flex w-full justify-center'
                >
                    <NewsletterForm source='coming-soon' />
                </Reveal>

                <Reveal
                    immediate
                    delay={0.8}
                    y={12}
                    blur={6}
                    duration={0.7}
                >
                    <Link
                        href='/'
                        className='inline-flex items-center gap-2 text-[15px] tracking-[-0.01em] text-white/56 transition-colors duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:text-white'
                    >
                        <ArrowLeft
                            aria-hidden='true'
                            className='size-4'
                        />

                        Back to home
                    </Link>
                </Reveal>
            </div>
        </main>
    )
}

export default ComingSoon
